import { useState, useMemo } from 'react';
import {
  ArrowUpRight,
  ArrowDownRight,
  DollarSign,
  TrendingUp,
  ShoppingCart,
  Users,
  Tag,
  UserCheck,
  Package,
  Crown,
} from 'lucide-react';
import {
  Line,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import type {
  SimpleMetric,
  PercentageMetric,
  TopProductItem,
  TopSalespersonItem,
  GrossProfitChartPoint,
  RetailMetricsData,
} from '@/hooks/useRetailMetrics';
import { RetailWidgetKey } from '@/hooks/useDashboardPreferences';

// ---- Shared sub-components ----

function MetricChange({ change, comparisonLabel }: { change: number; comparisonLabel: string }) {
  const isUp = change >= 0;
  return (
    <div className="flex items-center text-xs mt-1.5">
      {isUp ? (
        <ArrowUpRight className="mr-0.5 h-3.5 w-3.5 text-emerald-500" />
      ) : (
        <ArrowDownRight className="mr-0.5 h-3.5 w-3.5 text-red-500" />
      )}
      <span className={isUp ? 'text-emerald-500' : 'text-red-500'}>
        {isUp ? '+' : ''}{change.toFixed(1)}%
      </span>
      <span className="ml-1 text-muted-foreground">vs {comparisonLabel}</span>
    </div>
  );
}

function CardSkeleton() {
  return (
    <Card className="animate-pulse">
      <CardContent className="pt-6">
        <div className="h-4 w-24 bg-muted rounded mb-3" />
        <div className="h-8 w-32 bg-muted rounded mb-2" />
        <div className="h-3 w-28 bg-muted rounded" />
      </CardContent>
    </Card>
  );
}

// ---- Individual metric cards ----

function RevenueCard({
  data,
  formatCurrency,
}: {
  data: SimpleMetric;
  formatCurrency: (v: number) => string;
}) {
  const diff = data.value - data.previousValue;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <DollarSign className="h-3.5 w-3.5" />
          Revenue
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tracking-tight">{formatCurrency(data.value)}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {formatCurrency(Math.abs(diff))} {diff >= 0 ? 'more' : 'less'} than {data.comparisonLabel}
        </p>
        <MetricChange change={data.change} comparisonLabel={data.comparisonLabel} />
      </CardContent>
    </Card>
  );
}

function GrossProfitCard({
  data,
  formatCurrency,
}: {
  data: SimpleMetric & { chartData: GrossProfitChartPoint[] };
  formatCurrency: (v: number) => string;
}) {
  const diff = data.value - data.previousValue;
  const chartConfig: ChartConfig = {
    grossProfit: { label: 'Gross profit', color: '#3b82f6' },
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <TrendingUp className="h-3.5 w-3.5" />
          Gross profit
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tracking-tight">{formatCurrency(data.value)}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {formatCurrency(Math.abs(diff))} {diff >= 0 ? 'more' : 'less'} than {data.comparisonLabel}
        </p>
        <MetricChange change={data.change} comparisonLabel={data.comparisonLabel} />

        {data.chartData.length > 1 && (
          <div className="mt-4 h-32">
            <ChartContainer config={chartConfig} className="h-full w-full">
              <LineChart data={data.chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" vertical={false} />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`}
                  width={40}
                />
                <ChartTooltip
                  cursor={{ stroke: 'hsl(var(--muted))', strokeWidth: 1 }}
                  content={
                    <ChartTooltipContent
                      indicator="dot"
                      valueFormatter={(v) => formatCurrency(v)}
                    />
                  }
                />
                <Line
                  type="monotone"
                  dataKey="grossProfit"
                  stroke="var(--color-grossProfit)"
                  strokeWidth={2}
                  dot={{ r: 3, fill: 'var(--color-grossProfit)' }}
                  activeDot={{ r: 5, strokeWidth: 0, fill: 'var(--color-grossProfit)' }}
                />
              </LineChart>
            </ChartContainer>
          </div>
        )}

        <p className="text-xs text-primary mt-2 cursor-pointer hover:underline">
          View report
        </p>
      </CardContent>
    </Card>
  );
}

function SaleCountCard({ data }: { data: SimpleMetric }) {
  const diff = data.value - data.previousValue;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <ShoppingCart className="h-3.5 w-3.5" />
          Sale count
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tracking-tight">{data.value.toLocaleString()}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {Math.abs(diff).toLocaleString()} {diff >= 0 ? 'more' : 'fewer'} than {data.comparisonLabel}
        </p>
        <MetricChange change={data.change} comparisonLabel={data.comparisonLabel} />
      </CardContent>
    </Card>
  );
}

function SalesWithCustomerDetailsCard({ data }: { data: PercentageMetric }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <UserCheck className="h-3.5 w-3.5" />
          Sales with customer details
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tracking-tight">{data.percentage.toFixed(1)}%</p>
        <Progress value={data.percentage} className="mt-2 h-2" />
        <p className="text-sm text-muted-foreground mt-2">
          {data.count} of {data.total} sales have customer details
        </p>
        <div className="flex items-center text-xs mt-1.5">
          {data.percentage >= data.previousPercentage ? (
            <ArrowUpRight className="mr-0.5 h-3.5 w-3.5 text-emerald-500" />
          ) : (
            <ArrowDownRight className="mr-0.5 h-3.5 w-3.5 text-red-500" />
          )}
          <span className={data.percentage >= data.previousPercentage ? 'text-emerald-500' : 'text-red-500'}>
            {data.previousPercentage.toFixed(1)}%
          </span>
          <span className="ml-1 text-muted-foreground">{data.comparisonLabel}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function AvgItemsPerSaleCard({ data }: { data: SimpleMetric }) {
  const diff = data.value - data.previousValue;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5" />
          Avg. items per sale
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tracking-tight">{data.value.toFixed(1)}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {Math.abs(diff).toFixed(2)} {diff >= 0 ? 'more' : 'less'} than {data.comparisonLabel}
        </p>
        <MetricChange change={data.change} comparisonLabel={data.comparisonLabel} />
      </CardContent>
    </Card>
  );
}

function AvgSaleValueCard({
  data,
  formatCurrency,
}: {
  data: SimpleMetric;
  formatCurrency: (v: number) => string;
}) {
  const diff = data.value - data.previousValue;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <DollarSign className="h-3.5 w-3.5" />
          Avg. sale value
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tracking-tight">{formatCurrency(data.value)}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {formatCurrency(Math.abs(diff))} {diff >= 0 ? 'more' : 'less'} than {data.comparisonLabel}
        </p>
        <MetricChange change={data.change} comparisonLabel={data.comparisonLabel} />
      </CardContent>
    </Card>
  );
}

function DiscountedCard({
  data,
  formatCurrency,
}: {
  data: SimpleMetric & { discountedOrderCount: number; totalOrderCount: number };
  formatCurrency: (v: number) => string;
}) {
  const pct = data.totalOrderCount > 0
    ? ((data.discountedOrderCount / data.totalOrderCount) * 100).toFixed(1)
    : '0.0';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <Tag className="h-3.5 w-3.5" />
          Discounted
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tracking-tight">{formatCurrency(data.value)}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {data.discountedOrderCount} of {data.totalOrderCount} sales ({pct}%) had discounts
        </p>
        <MetricChange change={data.change} comparisonLabel={data.comparisonLabel} />
      </CardContent>
    </Card>
  );
}

function MySalesPerformanceCard({
  data,
  formatCurrency,
}: {
  data: SimpleMetric & { orderCount: number; previousOrderCount: number };
  formatCurrency: (v: number) => string;
}) {
  const diff = data.value - data.previousValue;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" />
          My sales performance
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tracking-tight">{formatCurrency(data.value)}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {formatCurrency(Math.abs(diff))} {diff >= 0 ? 'more' : 'less'} than {data.comparisonLabel}
        </p>
        <div className="flex gap-4 mt-3 pt-3 border-t text-sm">
          <div>
            <span className="text-muted-foreground">Orders: </span>
            <span className="font-medium">{data.orderCount}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Prev: </span>
            <span className="font-medium">{data.previousOrderCount}</span>
          </div>
        </div>
        <MetricChange change={data.change} comparisonLabel={data.comparisonLabel} />
      </CardContent>
    </Card>
  );
}

function TopProductsSoldCard({
  data,
  formatCurrency,
}: {
  data: TopProductItem[];
  formatCurrency: (v: number) => string;
}) {
  const [tab, setTab] = useState<'quantity' | 'revenue'>('quantity');

  const sorted = useMemo(() => {
    const items = [...data];
    if (tab === 'quantity') {
      items.sort((a, b) => b.quantity - a.quantity);
    } else {
      items.sort((a, b) => b.revenue - a.revenue);
    }
    return items.slice(0, 5);
  }, [data, tab]);

  return (
    <Card className="col-span-1">
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <Crown className="h-3.5 w-3.5" />
          Top products sold
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Tabs */}
        <div className="flex gap-4 mb-3 border-b">
          <button
            onClick={() => setTab('quantity')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'quantity'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            By quantity
          </button>
          <button
            onClick={() => setTab('revenue')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'revenue'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            By revenue
          </button>
        </div>

        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No product data yet</p>
        ) : (
          <div className="space-y-3">
            {sorted.map((product, idx) => (
              <div key={product.productId || idx} className="flex items-center gap-3">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="h-9 w-9 rounded-lg object-cover bg-muted"
                  />
                ) : (
                  <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                    <Package className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{product.name}</p>
                  {product.sku && (
                    <p className="text-xs text-muted-foreground">{product.sku}</p>
                  )}
                </div>
                <span className="text-sm font-semibold tabular-nums">
                  {tab === 'quantity'
                    ? product.quantity.toLocaleString()
                    : formatCurrency(product.revenue)}
                </span>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-primary mt-3 cursor-pointer hover:underline">
          View report
        </p>
      </CardContent>
    </Card>
  );
}

function TopSalesPeopleCard({
  data,
  formatCurrency,
}: {
  data: TopSalespersonItem[];
  formatCurrency: (v: number) => string;
}) {
  const sorted = useMemo(() => {
    return [...data].sort((a, b) => b.revenue - a.revenue).slice(0, 5);
  }, [data]);

  return (
    <Card className="col-span-1">
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <Crown className="h-3.5 w-3.5" />
          Top sales people
        </CardTitle>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No sales data yet</p>
        ) : (
          <div className="space-y-3">
            {sorted.map((person) => (
              <div key={person.userId} className="flex items-center gap-3">
                {person.avatarUrl ? (
                  <img
                    src={person.avatarUrl}
                    alt={person.name}
                    className="h-9 w-9 rounded-full object-cover bg-muted"
                  />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{person.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {person.orderCount} {person.orderCount === 1 ? 'sale' : 'sales'}
                  </p>
                </div>
                <span className="text-sm font-semibold tabular-nums">
                  {formatCurrency(person.revenue)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Main renderer ----

interface RetailMetricCardsProps {
  enabledWidgets: RetailWidgetKey[];
  metricsData: RetailMetricsData;
  formatCurrency: (v: number) => string;
}

export function RetailMetricCards({
  enabledWidgets,
  metricsData,
  formatCurrency,
}: RetailMetricCardsProps) {
  if (enabledWidgets.length === 0) return null;

  if (metricsData.isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {enabledWidgets.slice(0, 4).map((_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Render cards in the order they appear in the enabled list
  const renderCard = (key: RetailWidgetKey) => {
    switch (key) {
      case 'revenue':
        return metricsData.revenue ? (
          <RevenueCard key={key} data={metricsData.revenue} formatCurrency={formatCurrency} />
        ) : null;
      case 'gross_profit':
        return metricsData.grossProfit ? (
          <GrossProfitCard key={key} data={metricsData.grossProfit} formatCurrency={formatCurrency} />
        ) : null;
      case 'sale_count':
        return metricsData.saleCount ? (
          <SaleCountCard key={key} data={metricsData.saleCount} />
        ) : null;
      case 'sales_with_customer_details':
        return metricsData.salesWithCustomerDetails ? (
          <SalesWithCustomerDetailsCard key={key} data={metricsData.salesWithCustomerDetails} />
        ) : null;
      case 'avg_items_per_sale':
        return metricsData.avgItemsPerSale ? (
          <AvgItemsPerSaleCard key={key} data={metricsData.avgItemsPerSale} />
        ) : null;
      case 'avg_sale_value':
        return metricsData.avgSaleValue ? (
          <AvgSaleValueCard key={key} data={metricsData.avgSaleValue} formatCurrency={formatCurrency} />
        ) : null;
      case 'discounted':
        return metricsData.discounted ? (
          <DiscountedCard key={key} data={metricsData.discounted} formatCurrency={formatCurrency} />
        ) : null;
      case 'my_sales_performance':
        return metricsData.mySalesPerformance ? (
          <MySalesPerformanceCard key={key} data={metricsData.mySalesPerformance} formatCurrency={formatCurrency} />
        ) : null;
      case 'top_products_sold':
        return metricsData.topProductsSold ? (
          <TopProductsSoldCard key={key} data={metricsData.topProductsSold} formatCurrency={formatCurrency} />
        ) : null;
      case 'top_sales_people':
        return metricsData.topSalesPeople ? (
          <TopSalesPeopleCard key={key} data={metricsData.topSalesPeople} formatCurrency={formatCurrency} />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {enabledWidgets.map(renderCard)}
    </div>
  );
}
